import React, { useEffect, useState, useCallback } from "react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Trash2, StickyNote, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { tr } from "../i18n/LangContext";

function relTime(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "less than a minute ago";
    if (diff < 3600) { const m = Math.floor(diff / 60); return `${m} minute${m > 1 ? "s" : ""} ago`; }
    if (diff < 86400) { const h = Math.floor(diff / 3600); return `${h} hour${h > 1 ? "s" : ""} ago`; }
    const d = Math.floor(diff / 86400);
    if (d < 30) return `${d} day${d > 1 ? "s" : ""} ago`;
    return new Date(iso).toLocaleDateString();
  } catch (e) { return iso; }
}

export default function PnlNotesDialog({ open, onOpenChange, revenueId, name }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!revenueId) return;
    setLoading(true);
    try {
      const r = await client.get("/notes", { params: { entity_type: "pnl_revenue", entity_id: revenueId } });
      setNotes(r.data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [revenueId]);
  useEffect(() => { if (open) { setText(""); load(); } }, [open, load]);

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setSaving(true);
    try {
      const r = await client.post("/notes", { entity_type: "pnl_revenue", entity_id: revenueId, text: t });
      setNotes((prev) => [r.data, ...prev]);
      setText("");
    } catch (e) { toast.error(tr("Couldn't save note"), { description: e?.response?.data?.detail }); }
    finally { setSaving(false); }
  };

  const remove = async (n) => {
    try {
      await client.delete(`/notes/${n.id}`);
      setNotes((prev) => prev.filter((x) => x.id !== n.id));
      toast.success(tr("Note deleted"));
    } catch (e) { toast.error(tr("Couldn't delete"), { description: e?.response?.data?.detail }); }
  };

  const isMine = (n) => n.author_user_id === user?.user_id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg glass-strong border-white/10" data-testid="pnl-notes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl text-white">
            <StickyNote className="h-5 w-5 text-brand-green" /> Notes — {name || "revenue"}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">{tr("Timestamped and attributed. Only you can delete what you write.")}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[46vh] space-y-2 overflow-y-auto pr-1">
          {loading ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{tr("Loading notes…")}</p>
          ) : notes.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{tr("No notes yet.")}</p>
          ) : notes.map((n) => (
            <div key={n.id} className={`rounded-xl border p-3 ${isMine(n) ? "border-brand-green/25 bg-brand-green/[0.04]" : "border-white/8 bg-white/[0.02]"}`} data-testid="pnl-note-item">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`font-mono text-[11px] font-semibold uppercase tracking-wide ${isMine(n) ? "text-brand-green" : "text-white"}`}>{isMine(n) ? "You" : n.author_name}</span>
                    <span className="font-mono text-[10px] text-muted-foreground/70">{relTime(n.created_at)}</span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{n.text}</p>
                </div>
                {isMine(n) && (
                  <button data-testid="pnl-note-delete" onClick={() => remove(n)}
                    className="shrink-0 text-muted-foreground transition-colors hover:text-brand-red"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-1 rounded-xl border border-white/8 bg-white/[0.02] p-3">
          <Textarea data-testid="pnl-note-input" value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder={tr("Add a note. Only you can delete what you write.")}
            className="min-h-[72px] resize-none border-white/10 bg-input/50 focus-visible:ring-brand-green" />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground/70">⌘/Ctrl+Enter to add</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => onOpenChange(false)}
                className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">{tr("Close")}</Button>
              <Button size="sm" data-testid="pnl-note-add" onClick={add} disabled={saving || !text.trim()}
                className="bg-brand-green hover:bg-brand-green/90 text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add note"}</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
